import { handleAdminInvoiceReportsAccessError } from '@/context/services/admin_invoice_reports/handleAdminInvoiceReportsAccessError';
import { useConvertToModernPagination } from '@/context/services/hooks/usePagination';
import {
  isAuthError,
  isForbiddenError,
  isSuccess,
} from '@/context/services/type';
import type {
  AdminInvoiceReportColumn,
  FetchAdminInvoiceReportsArgs,
} from '@/features/admin_invoice_reports/types';
import { ModernPaginationProps } from '@moneyforward/ap-frontend-components';
import {
  AdminInvoiceReportPaymentInfoResponse,
  CommonError,
  InvoiceReportFormInputResponse,
  SearchedAdminInvoiceReport,
  reportFormInputType,
  useGetAdminInvoiceReportPaymentInfoSuspense,
  useSearchAdminInvoiceReports,
} from 'ap-openapi';
import { useMemo } from 'react';

export { reportFormInputType };
export type {
  AdminInvoiceReportPaymentInfoResponse,
  InvoiceReportFormInputResponse,
};

const isCommonError = (value: unknown): value is CommonError =>
  typeof value === 'object' && value !== null;

const responseToAdminInvoiceReportColumn = (
  item: SearchedAdminInvoiceReport
): AdminInvoiceReportColumn => ({
  id: item.id,
  number: item.number,
  created_at: item.created_at,
  submitted_at: item.submitted_at,
  office_member: {
    name: item.office_member?.name || '',
    number: item.office_member?.number || '',
  },
  title: item.title,
  status: item.status || {},
  is_waiting: item.is_waiting,
  ap_payee_name: item.ap_payee_name,
  book_date: item.book_date,
  due_date: item.due_date,
  total_amount: item.total_amount,
  foreign_currency_amount: item.foreign_currency_amount,
  report_approval_names: item.report_approval_names,
  approved_at: item.approved_at,
  payment_date: item.payment_date,
  payment_status: item.payment_status,
  error_messages: item.error_messages,
  invoice_file: item.invoice_file
    ? {
        name: item.invoice_file.filename || '',
        receipt_type: item.invoice_file.receipt_type || '',
      }
    : undefined,
  detail_path: item.detail_path,
  edit_path: item.edit_path,
  edit_action: item.edit_action ?? {
    visible: false,
    allowed: false,
    reason: null,
  },
  delete_action: item.delete_action ?? {
    visible: false,
    allowed: false,
    reason: null,
  },
  cancel_action: item.cancel_action ?? {
    visible: false,
    allowed: false,
    reason: null,
  },
});

export function useAdminInvoiceReportsSearch(
  args: FetchAdminInvoiceReportsArgs = {}
) {
  const { page = 1, perPage = 50, query, order_by } = args;

  const { data, refetch, isFetching } = useSearchAdminInvoiceReports(
    {
      pagination: {
        per_page: perPage,
        page,
      },
      query,
      order_by: order_by
        ? order_by.map((item) => ({
            field: item.field,
            direction: item.direction,
          }))
        : undefined,
    },
    {},
    {
      query: {
        queryKey: ['adminInvoiceReports', { page, perPage, query, order_by }],
      },
    }
  );

  const pagination: ModernPaginationProps = useConvertToModernPagination(
    data?.data && typeof data.data === 'object' && 'pagination' in data.data
      ? data.data.pagination
      : undefined
  );

  const result: AdminInvoiceReportColumn[] = useMemo(
    () =>
      data?.data &&
      typeof data.data === 'object' &&
      'invoice_reports' in data.data
        ? data.data.invoice_reports?.map(responseToAdminInvoiceReportColumn) ??
          []
        : [],
    [data]
  );

  if (!isSuccess(data?.status)) {
    const responseData = data?.data;
    const code = isCommonError(responseData)
      ? responseData.code ?? undefined
      : undefined;
    handleAdminInvoiceReportsAccessError(data?.status, code);
  }

  return {
    pagination,
    data: result,
    refetch,
    isFetching,
    isError:
      !isSuccess(data?.status) &&
      !isAuthError(data?.status) &&
      !isForbiddenError(data?.status),
    error: data && isCommonError(data.data) ? { ...data.data } : undefined,
  };
}

export const useGetAdminInvoiceReportPaymentInfo = (
  reportId: string
): { data: AdminInvoiceReportPaymentInfoResponse | undefined } => {
  const { data, error } = useGetAdminInvoiceReportPaymentInfoSuspense(reportId);
  if (error) throw error;
  if (isSuccess(data?.status)) {
    return { data: data?.data };
  } else {
    return { data: undefined };
  }
};
